import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  Checkbox,
  Fieldset,
  Flex,
  Group,
  Notification,
  Paper,
  Radio,
  Stack,
  Tabs,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { useParams } from 'react-router';
import { usePostQuestionnaires } from '../../../global/entities/Questionnaires/mutations/usePostQuestionnairesMutations.tsx';
import { usePutQuestionnaires } from '../../../global/entities/Questionnaires/mutations/usePutQuestionnairesMutations.tsx';
import {
  IQuestionnairesPayload,
  IQuestionScore,
  ICategoryConfig,
} from '../../../global/entities/Questionnaires/Questionnaires.ts';
import { useGetQuestionnaires } from '../../../global/entities/Questionnaires/queries/useGetQuestionnaires.tsx';
import { useGetQuestionnairesById } from '../../../global/entities/Questionnaires/queries/useGetQuestionnairesById.tsx';
import SurveyPreview from './SurveyPreview.tsx';
import { useNavigate } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import { theme } from '../../../global/style/theme.tsx';
import SurveyCategories from './SurveyCategories.tsx';
import SurveyCalculatedScoring from './SurveyCalculatedScoring.tsx';
import { scoringRegex } from './SurveyCategoriesRow.tsx';
import { TableContextProvider } from '../../../global/hooks/useTable.tsx';
import { css } from '@emotion/css';
import clsx from 'clsx';
import { QuestionnaireScoringMode } from '../../../global/types';

const animatedTabStyle = css`
  @keyframes redPulse {
    0% {
      color: red;
    }
    50% {
      color: inherit;
    }
    100% {
      color: red;
    }
  }

  animation: redPulse 1s ease-in-out infinite;
  font-weight: 600;
`;

const SurveyForm: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    'edit' | 'preview' | 'category' | 'calculatedScoring'
  >('edit');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [parsedJson, setParsedJson] = useState<any>(null);
  const [scores, setScores] = useState<IQuestionScore[]>([]);
  const [categoryConfigs, setCategoryConfigs] = useState<ICategoryConfig[]>([]);
  const params = useParams<{ id: string; tabId: string }>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const postQuestionnaires = usePostQuestionnaires();
  const putQuestionnaires = usePutQuestionnaires();
  const { refetch: refetchQuestionnaires } = useGetQuestionnaires();
  const { data: questionnaireData } = useGetQuestionnairesById();
  const navigate = useNavigate();
  const [showErrors, setShowErrors] = useState(false);

  const jsonSchema = z.string().superRefine((value, ctx) => {
    if (!value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('formError.requiredJson'),
      });
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (
        !parsed.title ||
        !parsed.description ||
        !Array.isArray(parsed.pages)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('formError.invalidJson'),
        });
      }
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('formError.invalidJson'),
      });
    }
  });

  const themeJsonSchema = z.string().superRefine((value, ctx) => {
    if (!value) return;

    try {
      JSON.parse(value);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('formError.invalidJson'),
      });
    }
  });

  const form = useForm({
    initialValues: {
      nameFr: '',
      nameEn: '',
      shortNameEn: '',
      shortNameFr: '',
      rawJson: '',
      themeJson: '',
      showInMedicalOverview: true,
      isPhysicalExamination: false,
      isMedicalHistory: false,
      isCounsellingNote: false,
      scoringMode: QuestionnaireScoringMode.Average,
      questionScores: [] as IQuestionScore[],
    },
    validate: zodResolver(
      z.object({
        nameFr: z.string().min(1, { message: t('formError.titleIsRequired') }),
        nameEn: z.string().min(1, { message: t('formError.titleIsRequired') }),
        shortNameFr: z
          .string()
          .min(1, { message: t('formError.shortNameFrIsRequired') }),
        shortNameEn: z
          .string()
          .min(1, { message: t('formError.shortNameEnIsRequired') }),
        rawJson: jsonSchema,
        themeJson: themeJsonSchema,
        showInMedicalOverview: z.boolean(),
        isPhysicalExamination: z.boolean(),
        isMedicalHistory: z.boolean(),
        isCounsellingNote: z.boolean(),
        scoringMode: z.nativeEnum(QuestionnaireScoringMode),
      }),
    ),
  });

  const handleJsonChange = (value: string) => {
    form.setFieldValue('rawJson', value);

    if (!value) {
      setJsonError(t('formError.requiredJson'));
      setParsedJson(null);
      return;
    }

    try {
      const parsed = JSON.parse(value);

      if (
        !parsed.title ||
        !parsed.description ||
        !Array.isArray(parsed.pages)
      ) {
        setJsonError(t('formError.missingJsonFields'));
        setParsedJson(null);
        return;
      }

      setParsedJson(parsed);
      setJsonError(null);
    } catch (error: any) {
      const errorMessage = error.message.match(/position (\d+)/);
      const errorPosition = errorMessage ? Number(errorMessage[1]) : null;
      const errorLine = errorPosition
        ? value.substring(0, errorPosition).split('\n').length
        : '?';
      setJsonError(`${t('formError.invalidJsonLine')} ${errorLine}`);
      setParsedJson(null);
    }
  };

  const handleCancel = () => {
    form.reset();
    navigate('/adminDashboard/survey');
  };

  const handleSubmit = async (formValues: IQuestionnairesPayload) => {
    setError(null);
    setShowErrors(true);

    const hasErrors = scores.some(
      (q) =>
        !q.questionName ||
        !q.categoryEn ||
        !q.categoryFr ||
        !scoringRegex.test(q.scoring ?? ''),
    );

    if (hasErrors) {
      // prevent save
      return;
    }

    // Check for CategoryConfigs validation
    const hasCategoryErrors = categoryConfigs.some(
      (c) => !c.categoryFr || !c.categoryEn || !c.calculatedValueName,
    );

    if (hasCategoryErrors) {
      // prevent save
      return;
    }

    setIsSubmitting(true);
    const valuesAndCategories = {
      ...formValues,
      // Minify JSON blobs before sending: the API layer corrupts embedded
      // newlines (turns them into a literal backslash-n) when persisting
      // multi-line strings, so pretty-printed JSON must never be sent as-is.
      rawJson: formValues.rawJson
        ? JSON.stringify(JSON.parse(formValues.rawJson))
        : formValues.rawJson,
      themeJson: formValues.themeJson
        ? JSON.stringify(JSON.parse(formValues.themeJson))
        : formValues.themeJson,
      questionScores: scores,
      categoryConfigs: categoryConfigs,
    };
    try {
      await (
        params.id !== '0' ? putQuestionnaires : postQuestionnaires
      ).mutateAsync(valuesAndCategories);
    } catch (e) {
      setError(
        e instanceof AppMedError ? e.message : 'An unexpected error occurred.',
      );
      console.error(e);
    } finally {
      setIsSubmitting(false);
      refetchQuestionnaires().then(() => {
        handleCancel();
      });
    }
  };

  useEffect(() => {
    if (questionnaireData) {
      form.reset();
      setParsedJson(questionnaireData.rawJson);
      const data = questionnaireData as any;
      form.setValues({
        ...questionnaireData,
        themeJson: questionnaireData.themeJson ?? '',
        showInMedicalOverview: questionnaireData.showInMedicalOverview ?? false,
        isPhysicalExamination: questionnaireData.isPhysicalExamination ?? false,
        isMedicalHistory: questionnaireData.isMedicalHistory ?? false,
        isCounsellingNote: questionnaireData.isCounsellingNote ?? false,
        scoringMode: data.scoringMode ?? QuestionnaireScoringMode.Average,
      });

      setScores(
        (questionnaireData.questionScores =
          questionnaireData.questionScores.map((q, index) => ({
            id: q.id ?? index + 1,
            ...q,
          }))),
      );

      // Load CategoryConfigs
      setCategoryConfigs(
        (questionnaireData.categoryConfigs ?? []).map((c, index) => ({
          id: c.id ?? index + 1,
          ...c,
        })),
      );

      form.resetDirty();
    } else {
      form.setValues({ nameFr: '', nameEn: '', rawJson: '', themeJson: '' });
      setScores([]);
      setCategoryConfigs([]);
    }
  }, [questionnaireData, params.id]);

  return (
    <Stack pt={'xl'} gap={'lg'} h={'100%'}>
      <Flex align={'center'} justify={'space-between'}>
        <Button
          leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
          onClick={() => navigate('/adminDashboard/survey')}
          variant="transparent"
        >
          <Title c="black" order={4}>
            {questionnaireData
              ? `${i18n.language === 'fr' ? questionnaireData?.nameFr : questionnaireData?.nameEn}`
              : t('formSurvey.addQuestionnaire')}
          </Title>
        </Button>
      </Flex>
      <form onSubmit={form.onSubmit(handleSubmit)} style={{ height: '98%' }}>
        <Paper w="100%" h={'100%'}>
          <Tabs
            value={activeTab}
            onChange={(tab) =>
              setActiveTab(
                tab as 'edit' | 'preview' | 'category' | 'calculatedScoring',
              )
            }
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100% - 100px)',
            }}
          >
            <Tabs.List>
              <Tabs.Tab
                value="edit"
                className={clsx({
                  [animatedTabStyle]: !form.isValid() || jsonError,
                })}
              >
                {t('formSurvey.edit')}
              </Tabs.Tab>
              <Tabs.Tab value="preview" disabled={!parsedJson}>
                {t('formSurvey.preview')}
              </Tabs.Tab>
              <Tabs.Tab value="category" disabled={!parsedJson}>
                {t('table.survey.scoring')}
              </Tabs.Tab>
              <Tabs.Tab value="calculatedScoring" disabled={!parsedJson}>
                {t('formSurvey.calculatedScoring')}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="edit" pt="md" flex={1} h={'100%'}>
              <Stack gap={'lg'} style={{ height: '100%' }}>
                <Flex justify="space-between" gap={'md'}>
                  <TextInput
                    w={'50%'}
                    label={t('formSurvey.shortNameFr')}
                    {...form.getInputProps('shortNameFr')}
                    maxLength={10}
                  />
                  <TextInput
                    w={'50%'}
                    label={t('formSurvey.shortNameEn')}
                    {...form.getInputProps('shortNameEn')}
                    maxLength={10}
                  />
                </Flex>
                <Flex justify="space-between" gap={'md'}>
                  <TextInput
                    w={'50%'}
                    label={t('formSurvey.surveyTitleFr')}
                    {...form.getInputProps('nameFr')}
                  />
                  <TextInput
                    w={'50%'}
                    label={t('formSurvey.surveyTitleEn')}
                    {...form.getInputProps('nameEn')}
                  />
                </Flex>
                <Radio.Group
                  label={t('formSurvey.scoringMode')}
                  value={form.values.scoringMode?.toString()}
                  onChange={(value: string) =>
                    form.setFieldValue('scoringMode', parseInt(value))
                  }
                >
                  <Group mt="xs">
                    <Radio
                      value={QuestionnaireScoringMode.Average.toString()}
                      label={t('formSurvey.scoringModeAverage')}
                    />
                    <Radio
                      value={QuestionnaireScoringMode.Sum.toString()}
                      label={t('formSurvey.scoringModeSum')}
                    />
                  </Group>
                </Radio.Group>
                <Textarea
                  style={{ flex: 1 }}
                  styles={{
                    wrapper: { height: '100%' },
                    input: { height: '100%' },
                  }}
                  pb={'lg'}
                  label={t('formSurvey.surveyJson')}
                  minRows={100}
                  {...form.getInputProps('rawJson')}
                  onChange={(event) =>
                    handleJsonChange(event.currentTarget.value)
                  }
                />
                {jsonError && form.values.rawJson && (
                  <Notification withCloseButton={false} color="red">
                    {jsonError}
                  </Notification>
                )}
                <Textarea
                  style={{ flex: 1 }}
                  styles={{
                    wrapper: { height: '100%' },
                    input: { height: '100%' },
                  }}
                  pb={'lg'}
                  label={t('formSurvey.themeJson')}
                  minRows={100}
                  {...form.getInputProps('themeJson')}
                />
                <Stack my={'sm'} gap="sm">
                  <Checkbox
                    label={t('formSurvey.showInMedicalOverview')}
                    {...form.getInputProps('showInMedicalOverview', {
                      type: 'checkbox',
                    })}
                  />
                  <Fieldset legend={t('formSurvey.usageLegend')}>
                    <Radio.Group
                      value={
                        form.values.isPhysicalExamination
                          ? 'physicalExamination'
                          : form.values.isMedicalHistory
                            ? 'medicalHistory'
                            : form.values.isCounsellingNote
                              ? 'counsellingNote'
                              : 'none'
                      }
                      onChange={(value) => {
                        form.setFieldValue('isPhysicalExamination', value === 'physicalExamination');
                        form.setFieldValue('isMedicalHistory', value === 'medicalHistory');
                        form.setFieldValue('isCounsellingNote', value === 'counsellingNote');
                      }}
                    >
                      <Stack gap="xs">
                        <Radio value="none" label={t('formSurvey.usageNone')} />
                        <Radio
                          value="physicalExamination"
                          label={t('formSurvey.isPhysicalExamination')}
                        />
                        <Radio value="medicalHistory" label={t('formSurvey.isMedicalHistory')} />
                        <Radio value="counsellingNote" label={t('formSurvey.isCounsellingNote')} />
                      </Stack>
                    </Radio.Group>
                  </Fieldset>
                </Stack>
              </Stack>

              {error && (
                <Notification color="red" withCloseButton={false}>
                  {error}
                </Notification>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="preview" pt="md" style={{ overflow: 'hidden' }}>
              {parsedJson && (
                <SurveyPreview
                  surveyJson={parsedJson}
                  rawJson={form.values.rawJson}
                  themeJson={form.values.themeJson}
                />
              )}
            </Tabs.Panel>
            <Tabs.Panel value="category" pt="md" style={{ overflow: 'auto' }}>
              {scores && (
                <TableContextProvider>
                  <SurveyCategories
                    scores={scores}
                    onChangesScores={setScores}
                    showErrors={showErrors}
                  />
                </TableContextProvider>
              )}
            </Tabs.Panel>
            <Tabs.Panel
              value="calculatedScoring"
              pt="md"
              style={{ overflow: 'auto' }}
            >
              {categoryConfigs !== undefined && (
                <TableContextProvider>
                  <SurveyCalculatedScoring
                    configs={categoryConfigs}
                    onChangesConfigs={setCategoryConfigs}
                    showErrors={showErrors}
                    rawJson={form.values.rawJson}
                  />
                </TableContextProvider>
              )}
            </Tabs.Panel>
          </Tabs>

          <Group mt="md" justify="flex-end">
            <Button
              variant={'default'}
              color={'gray'}
              onClick={() => handleCancel()}
            >
              {t('button.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {t(params.id ? 'button.save' : 'button.save')}
            </Button>
          </Group>
        </Paper>
      </form>
    </Stack>
  );
});

export default SurveyForm;
